import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, HelpCircle } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { ANSWERS, ANSWER_CATEGORIES, getAnswersByCategory } from "@/lib/answersData";
import { SCHEMA_ORG_ID, buildSpeakableSchema } from "@/lib/entitySchema";

const SITE_URL = "https://coloradowebimpressions.com";

const HUB_URL = `${SITE_URL}/answers`;
const HUB_TITLE = "Colorado Marketing Answers | SEO, PPC & Web Design Q&A";
const HUB_DESCRIPTION = `${ANSWERS.length} direct answers to the most common questions Colorado business owners ask about SEO, Google Ads, web design, Google Business Profile, AI search, and hiring a marketing agency.`;

function buildHubHead() {
  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": HUB_URL,
    url: HUB_URL,
    name: HUB_TITLE,
    description: HUB_DESCRIPTION,
    isPartOf: { "@id": SCHEMA_ORG_ID },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: ANSWERS.length,
      itemListElement: ANSWERS.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/answers/${a.slug}`,
        name: a.question,
      })),
    },
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Answers", item: HUB_URL },
    ],
  };

  return {
    meta: [
      { title: HUB_TITLE },
      { name: "description", content: HUB_DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: HUB_TITLE },
      { property: "og:description", content: HUB_DESCRIPTION },
      { property: "og:url", content: HUB_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: HUB_TITLE },
      { name: "twitter:description", content: HUB_DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: HUB_URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(collection) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
      { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(HUB_URL, HUB_TITLE)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${HUB_URL}#article`,
          headline: HUB_TITLE,
          description: HUB_DESCRIPTION,
          url: HUB_URL,
          image: `${SITE_URL}/logo.png`,
          datePublished: "2025-09-01",
          dateModified: new Date().toISOString().slice(0, 10),
          author: { "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh` },
          publisher: { "@id": SCHEMA_ORG_ID },
          mainEntityOfPage: HUB_URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${HUB_URL}#faq`,
          mainEntity: ANSWERS.slice(0, 10).map((a) => ({
            "@type": "Question",
            name: a.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: a.shortAnswer ?? a.question,
              url: `${SITE_URL}/answers/${a.slug}`,
            },
          })),
        }),
      },
    ],
  };
}

export const Route = createFileRoute("/answers/")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => buildHubHead(),
  component: AnswersHubPage,
});

function AnswersHubPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Answers Hub"
        title={
          <>
            Straight answers to <span className="text-gradient-gold">{ANSWERS.length} questions</span> Colorado business owners actually ask
          </>
        }
        description="Pricing, timelines, what works, what doesn't — for SEO, Google Ads, Google Business Profile, web design, AI search, and hiring an agency. No fluff. Updated regularly."
      />

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
            <div className="flex items-start gap-3">
              <HelpCircle className="mt-1 h-5 w-5 shrink-0 text-brand-gold-deep" />
              <p className="text-sm leading-relaxed text-muted-foreground" data-speakable>
                Every question here gets a 40–80 word lift-ready answer at the top, supporting bullets, a long-form explanation, and 5+ follow-up questions. Built so a real human can scan and decide — and so AI engines (ChatGPT, Perplexity, Google AI Overviews) can cite the answers cleanly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 space-y-12">
          {ANSWER_CATEGORIES.map((category) => {
            const items = getAnswersByCategory(category);
            if (items.length === 0) return null;
            return (
              <div key={category}>
                <h2 className="text-2xl font-extrabold text-foreground">
                  {category}
                  <span className="ml-2 text-sm font-medium text-muted-foreground">
                    ({items.length})
                  </span>
                </h2>
                <ul className="mt-5 space-y-2.5">
                  {items.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to="/answers/$slug"
                        params={{ slug: a.slug }}
                        className="group flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-smooth hover:border-brand-gold/60"
                      >
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep transition-smooth group-hover:translate-x-1" />
                        <span className="text-sm font-semibold text-foreground group-hover:text-brand-gold-deep">
                          {a.question}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Don't see your question?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Ask us directly. We'll answer honestly — and if it's a question others have, we'll add it to this hub.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold"
            >
              Ask a question
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60"
            >
              Browse the full FAQ
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
